import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import {
  CharlieDialog,
  CharlieDialogContent,
  CharlieButton,
  CharlieQrCode,
} from '@ver.id/libs.ui.charlie';
import { useTranslation } from '../i18n/i18n';
import appStoreBadge from '../assets/app-store.svg';
import playStoreBadge from '../assets/play-store.svg';

/**
 * InstallInstructionsModal — App install instructions with QR + store badges.
 *
 * Figma screens:
 *   Desktop available (Journey 5):     https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=73-9175
 *   Desktop unavailable (Journey 5):   https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=73-9250
 *   Mobile available (Journey 5):      https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=208-52997
 *   Mobile unavailable (Journey 5):    https://www.figma.com/design/6V1D3oNy17pfFNHIRb2nmm/Ronan-V2---UX?node-id=208-53241
 */

type InstallLinks = { android?: string; apple?: string };

export const installLinksMap: Record<string, InstallLinks> = {
  Yivi: {
    android:
      'https://play.google.com/store/apps/details?id=org.irmacard.cardemu',
    apple: 'https://apps.apple.com/app/yivi/id1294092994',
  },
  Datakeeper: {
    android: 'https://play.google.com/store/apps/details?id=nl.rabobank.ida',
    apple: 'https://apps.apple.com/app/datakeeper/id1546059184',
  },
  Digidentity: {
    android: 'https://play.google.com/store/apps/details?id=com.digidentity',
    apple: 'https://apps.apple.com/app/digidentity-wallet/id916749732',
  },
  Truid: {
    android: 'https://play.google.com/store/apps/details?id=me.veritru',
    apple: 'https://apps.apple.com/app/truid/id1610295519',
  },
  Yoti: {
    android:
      'https://play.google.com/store/apps/details?id=com.yoti.mobile.android.live',
    apple: 'https://apps.apple.com/app/yoti-your-digital-identity/id983980808',
  },
  Nect: {
    android: 'https://play.google.com/store/apps/details?id=com.nect.app.prod',
    apple: 'https://apps.apple.com/app/nect-wallet/id1331065474',
  },
  'ReadID Ready': {
    android: 'https://play.google.com/store/apps/details?id=com.readid.ready',
    apple: 'https://apps.apple.com/app/readid-ready/id1486164875',
  },
  Lissi: {
    android:
      'https://play.google.com/store/apps/details?id=io.lissi.mobile.android&hl=nl',
    apple: 'https://apps.apple.com/nl/app/lissi-id-wallet/id6475958390',
  },
};

type Props = {
  open: boolean;
  appName?: string;
  appNameWithArticle?: string;
  onClose: () => void;
};

export default function InstallInstructionsModal({
  open,
  appName,
  appNameWithArticle,
  onClose,
}: Props) {
  const { t } = useTranslation();

  if (!open) return null;

  const links = appName ? installLinksMap[appName] : undefined;
  const displayName = appNameWithArticle || appName || 'your identity app';
  const qrUrl = links ? `${window.location.origin}/?app=${appName}` : undefined;

  return (
    <CharlieDialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <CharlieDialogContent>
        {links ? (
          <>
            <div className="flex flex-col gap-2 items-center text-center">
              <h2 className="text-xl font-semibold text-foreground">
                {t('installInstructions.title', { appName: displayName })}
              </h2>
              <p className="text-sm text-muted-foreground max-w-xs">
                {t('installInstructions.subtitle', { appName: displayName })}
              </p>
            </div>

            <div className="flex flex-col gap-6 items-center">
              {qrUrl && <CharlieQrCode data={qrUrl} />}
              <div className="flex items-center justify-center gap-3 h-8">
                {links.apple && (
                  <a
                    href={links.apple}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-full"
                  >
                    <img
                      src={appStoreBadge}
                      alt="Download on the App Store"
                      className="h-full w-auto"
                    />
                  </a>
                )}
                {links.android && (
                  <a
                    href={links.android}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-full"
                  >
                    <img
                      src={playStoreBadge}
                      alt="Get it on Google Play"
                      className="h-full w-auto"
                    />
                  </a>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-2 items-center text-center">
            <FontAwesomeIcon
              icon={faCircleInfo}
              className="size-10 text-muted-foreground"
            />
            <h2 className="text-xl font-semibold text-foreground">
              {t('installInstructions.unavailableTitle')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('installInstructions.unavailableBody')}
            </p>
          </div>
        )}

        <CharlieButton variant="outline" pill block onClick={onClose}>
          {t('installInstructions.close')}
        </CharlieButton>
      </CharlieDialogContent>
    </CharlieDialog>
  );
}
